import gql from 'graphql-tag';

export const FRAGMENT_BOOKING_GUARANTORS = gql`
  fragment fragmentQueryBookingGuarantors on Contact {
    id
    relationship
    firstName
    middleName
    lastName
    title
    dateOfBirth
    countryTerritory
    cityTown
    cityTownId
    countryTerritoryId
    taxCountryTerritory
    taxCountryTerritoryId
    postcode
    addressLine1
    addressLine2
    mobilePhoneAreaCode
    mobilePhoneNumber
    mobilePhone
    homePhoneAreaCode
    homePhoneNumber
    homePhone
    contactEmail
    customFieldValues {
      id
      customFieldId
      customFieldType
      labelName
      value
      customFieldInputOnBookingJourney
      customFieldMandatoryOnBookingJourney
    }
    documents {
      id
      documentSettingId
      name
      status
      stage
      fileUuid
      guarantorId
      category
      mandatory
      description
      rejectedReason
    }
    guarantorReferenceNumber
    guarantorType
  }
`;

const FRAGMENT_BOOKING_LINK_BOOKING = gql`
  fragment fragmentQueryBookingLink on Booking {
    id
    originId
    specialRateOffered
    totalPrice
    bookingAdditionalFees {
      additionalFeeCardConfigId
      name
      dueDate
      dueAmount
      installmentIndex
      installmentNumber
      feature
      paymentItem {
        status
        paidAmount
      }
    }
    checkOutCartInfo {
      totalAmountToBePaid
      paymentDueNow {
        amountDue
        details {
          name
          amount
          description
        }
      }
      bookingTotalFeeDetail {
        totalFee
      }
      paymentToDate
    }
    installmentPlans {
      id
      name
      dueDate
      amountDue
      isDueBeforeBookingConfirmed
      partOfRentAdditionalFeeDeduct
      partOfRentAdditionalFees {
        name
        amount
        paymentItem {
          paidAmount
        }
      }
      paymentItem {
        id
        currency
        paidAmount
        totalAmount
        status
      }
    }
    tenancyOption {
      tenancyOptionId
      name
      startDate
      endDate
    }
    installment {
      name
    }
    bookingSpecialOffers {
      id
      specialOfferId
      specialOfferName
    }
    bookingAdditionalFees {
      additionalFeeCardConfigId
      name
      dueDate
      dueAmount
      installmentIndex
      installmentNumber
      feature
      paymentItem {
        status
        paidAmount
      }
    }
  }
`;

export const FRAGMENT_BOOKING_LINK = gql`
  fragment fragmentBookingLink on BookingLink {
    originalBooking {
      ...fragmentQueryBookingLink
    }
    replacementBooking {
      ...fragmentQueryBookingLink
    }
    occupiedBooking {
      ...fragmentQueryBookingLink
    }
    countDown
    type
    status
    sourceAcceptedAt
    sourceRejectedAt
    targetAcceptedAt
    targetRejectedAt
  }
  ${FRAGMENT_BOOKING_LINK_BOOKING}
`;

export const FRAGMENT_BOOKING = gql`
  fragment fragmentQueryBooking on Booking {
    id
    bookingJourneySkipped
    bookingLink {
      status
    }
    bookingAdditionalFees {
      name
      dueAmount
    }
    category
    isBedChangeableInSp
    nominationPlan {
      id
      partner {
        id
        name
        type
      }
      studentPayTo
    }
    roomType {
      roomTypeId
      name
    }
    academicYear {
      id
      name
    }
    serviceSelections {
      id
      serviceId
      serviceOptionId
      serviceName
      serviceType
      serviceOptionName
      incrementPerNight
    }
    chargingStartDate
    chargingEndDate
    totalPriceAfterService
    pricePerNightAfterService
    specialRateOffered
    status
    heldToTime
    countDown
    pricePerNightOriginal
    pricePerMonthOriginal
    totalPrice
    totalPriceOriginal
    totalPriceAfterTax
    checkInDate
    checkOutDate
    referral
    installment {
      installmentId
      installmentType
      name
    }
    student {
      id
      email
      account {
        id
      }
    }
    balanceInfo {
      walletBalance
      amendmentCreditBalance
    }
    tenancyOptionRateType
    tenancyLengthOption
    tenancyOption {
      tenancyOptionId
      name
      startDate
      endDate
      length
      intervalForTurnaround
    }
    property {
      id
      originId
      name
      imageName
      postalCode
      addressLine1
      addressLine2
      contactEmailAddress
      contactAreaCode
      contactPhoneNumber
      managementCompanyName
      billingCycle
      currency
      areaUnitOfMeasurement
      ownerName
      country {
        id
        name
        slug
        countryCode
      }
      city {
        id
        name
        slug
        latitude
        longitude
      }
      country {
        id
        slug
        name
      }
      buildings {
        id
        originId
        name
        floors {
          id
          originId
          name
          index
        }
      }
      flatPreferencePlanSetting {
        smokingEnabled
        petEnabled
        dietaryEnabled
        genderEnabled
      }
      paymentSettings {
        paymentChannel
        paymentMethod
        isEnabled
      }
    }
    roomAllocation {
      bedId
      bedName
      flatName
      floorName
      roomName
      buildingName
    }
    contracts {
      id
      name
      content
    }
    installmentPlans {
      id
      dueDate
      amountDue
      isDueBeforeBookingConfirmed
      partOfRentAdditionalFeeDeduct
      partOfRentAdditionalFees {
        name
      }
    }
    invoicedPaymentItems {
      id
      modelType
      category
      description
      currency
      paidAmount
      totalAmount
      status
      invoiceDatetime
      isDueBeforeBookingConfirmed
      chargeCreated
    }
    checkOutCartInfo {
      totalAmountToBePaid
      paymentDueNow {
        amountDue
        details {
          name
          amount
          description
        }
      }
      bookingTotalFeeDetail {
        rent {
          tenancyLength
          amountAfterTax
          originalAmount
          taxInfo {
            taxRate
            amount
            includedInPrice
          }
        }
        services {
          name
          amountAfterTax
          originalAmount
          taxInfo {
            taxRate
            amount
            includedInPrice
          }
        }
        additionalFees {
          name
          amountAfterTax
          originalAmount
          taxInfo {
            taxRate
            amount
            includedInPrice
          }
        }
        specialOffer {
          name
          amount
        }
        refundableFees {
          name
          amount
        }
        totalFee
      }
    }
    defaultCard {
      id
      type
      expireYear
      expireMonth
      cardBrand
      last4
      funding
      originId
    }
    backupCard {
      id
      type
      expireYear
      expireMonth
      cardBrand
      last4
      funding
      originId
    }
    paymentItems {
      id
      name
      status
    }
    documents {
      id
      name
      status
      stage
      fileUuid
      category
      guarantorId
      mandatory
      description
      rejectedReason
    }
    guarantorSettings {
      id
      academicYearId
      guarantorType
      guarantorTypeLabelName
      guarantorTypeDescription
    }
    currentGuarantorSetting {
      id
      academicYearId
      guarantorType
      guarantorTypeLabelName
      guarantorTypeDescription
    }
    curationTicket {
      id
      status
    }
  }
`;

export const MUTATION_CREATE_BOOKING = gql`
  mutation SP_Create_Booking($input: studentCreateBookingInput!) {
    studentCreateBooking(input: $input) {
      id
    }
  }
`;

export const STUDENT_GET_BOOKING = gql`
  query SP_Student_Get_Booking($id: ID!) {
    studentGetBooking(id: $id) {
      ...fragmentQueryBooking
    }
  }
  ${FRAGMENT_BOOKING}
`;

export const STUDENT_GET_BOOKING_PAYMENT_ITEMS = gql`
  query SP_Student_Get_Booking_Payment_Items($id: ID!) {
    studentGetBooking(id: $id) {
      id
      paymentItems {
        id
        name
        status
      }
    }
  }
`;

export const GET_CHARGE_STATUS = gql`
  query SP_GetChargeStatus($chargeId: ID!) {
    getChargeStatus(chargeId: $chargeId) {
      status
    }
  }
`;

export const UPDATE_INSEPCTION_REPORT = gql`
  mutation SP_MutationUpdateInspectionReport($input: studentUpdateInspectionReportInput!) {
    studentUpdateInspectionReport(input: $input) {
      id
    }
  }
`;

export const GET_PROPERTY_PLATFORM_ACCOUNT = gql`
  query SP_GetPropertyPlatformAccount($propertyId: ID!) {
    getPropertyPlatformAccount(propertyId: $propertyId) {
      id
      name
      currency
      publishableKey
    }
  }
`;

export const GET_PERSONAL_DETAILS_FIELD_SETTING_FOR_STUDENT = gql`
  query SP_GetPersonalDetailsFieldSettingForStudent(
    $propertyId: ID!
    $academicYearId: ID!
    $model: ModelOptions!
  ) {
    getPersonalDetailsFieldSettings(
      propertyId: $propertyId
      academicYearId: $academicYearId
      model: $model
    ) {
      id
      propertyId
      academicYearId
      labelName
      fieldName
      fieldIndex
      fieldType
      inputOnBookingJourney
      mandatoryOnBookingJourney
      fieldFormat
      customFieldId
    }
  }
`;

export const STUDENT_GET_PAYMENT_PREVIEW = gql`
  query SP_StudentGetPaymentPreview($input: studentGetPaymentPreviewInput!) {
    studentGetPaymentPreview(input: $input) {
      preview {
        paymentAmount
        transactionFee
        totalAmount
      }
      paymentItemDetails {
        name
        amount
      }
      previewHash
    }
  }
`;

export const MUTATION_STUDENT_CONFIRM_PAYMENT = gql`
  mutation SP_StudentConfirmPayment($input: studentConfirmPaymentInput!) {
    studentConfirmPayment(input: $input) {
      id
      success
      clientSecret
      nextAction
      declineCode
    }
  }
`;

export const STUDENT_GET_ON_BEHALF_OF_FOR_PAYMENT = gql`
  query SP_StudentGetOnBehalfOfForPayment($input: studentGetOnBehalfOfForPaymentInput!) {
    studentGetOnBehalfOfForPayment(input: $input)
  }
`;

export const GET_GUARANTOR_SETTINGS_BY_ID = gql`
  query SP_GetGuarantorSettingsById($guarantorSettingId: ID!, $academicYearId: ID!) {
    studentGetGuarantorSettingsById(
      guarantorSettingId: $guarantorSettingId
      academicYearId: $academicYearId
    ) {
      fieldSettings {
        id
        propertyId
        academicYearId
        labelName
        fieldName
        fieldIndex
        fieldType
        inputOnBookingJourney
        mandatoryOnBookingJourney
        fieldFormat
      }

      guarantorSetting {
        id
        livingCountries {
          id
          name
        }
        taxCountries {
          id
          name
        }
      }

      customFields {
        id
        type
        fieldFormat
        labelName
        inputOnBookingJourney
        mandatoryOnBookingJourney
      }

      documentSettings {
        id
        name
        description
        category
        stage
        mandatory
      }
    }
  }
`;

export const STUDENT_GET_BOOKING_CUSTOM_FIELDS_VALUES = gql`
  query Student_Get_Booking_Custom_Fields_Values($id: ID!) {
    studentGetBooking(id: $id) {
      id
      customFieldValues {
        id
        customFieldId
        customFieldType
        labelName
        value
        customFieldInputOnBookingJourney
        customFieldMandatoryOnBookingJourney
      }
    }
  }
`;

export const STUDENT_GET_BOOKING_GUARANTORS = gql`
  query Student_Get_Booking_Guarantors($id: ID!) {
    studentGetBooking(id: $id) {
      id
      guarantors {
        ...fragmentQueryBookingGuarantors
      }
    }
  }
  ${FRAGMENT_BOOKING_GUARANTORS}
`;

export const GET_LANGUAGES = gql`
  query QueryLanguages {
    getLanguages {
      languages {
        language
        abbreviation
        region
        default
      }
    }
  }
`;

export const GET_CHARGE_INFO = gql`
  query SP_GetChargeInfo($propertyId: ID!, $filters: getChargeInfoInput!) {
    getChargeInfo(propertyId: $propertyId, filters: $filters) {
      chargInfo {
        id
        bookingId
        status
        amount
        currency
        paymentMethodType
        nextAction
        paymentItems {
          id
          description
          totalAmount
        }
      }
    }
  }
`;

export const GET_BOOKING_CURATION_TICKET = gql`
  query SP_GetBookingCurationTicket($id: ID!) {
    studentGetBooking(id: $id) {
      id
      curationTicket {
        id
        status
      }
    }
  }
`;

export const SHOULD_SHOW_SPECIAL_OFFER_CODE = gql`
  query ShouldShowSpecialOfferCode($propertyId: ID!, $academicYearId: DecodedAcademicYearID!) {
    shouldShowSpecialOfferCode(propertyId: $propertyId, academicYearId: $academicYearId) {
      shouldShow
    }
  }
`;
